import { asStringArray, groupRecords, pollResults, selectOption, tasksCollection, voteScore, type CollectionData, type RecordData } from '@tomo/core';
import { Avatar, Badge, Button, Card, EmptyState, Menu, StackedBar, Tag, type MenuItem } from '@tomo/ui';
import { ArrowRight, Clock, Eye, Image as ImageIcon, LayoutGrid, Lightbulb, Plus, Shapes, SquareCheck, Vote } from 'lucide-react';
import { useMemo, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router';
import { useAllRecords, useBoards, useCollections, useMeta, usePersonMap, usePolls, useProject, useProjectVersion, useReadOnly, useSession, useSyncState } from '../../data/hooks.tsx';
import { useIdentity } from '../../data/identity.ts';
import { Icon } from '../../lib/icons.tsx';
import { formatDate, relativeTime } from '../../lib/time.ts';
import { VoteButton } from '../fields/display.tsx';
import { ProjectTile } from '../shell/ProjectTile.tsx';
import { openNewCollection } from '../shell/ui-state.ts';
import { useOpenRecord } from '../windows/hooks.ts';
import { RecordLink } from '../windows/RecordLink.tsx';
import './overview.css';
import { capitalize, newRecordLabels } from '../collections/view-utils.ts';

export function OverviewPage() {
  const project = useProject();
  const session = useSession();
  const navigate = useNavigate();
  const meta = useMeta();
  const sync = useSyncState();
  const collections = useCollections();
  const base = `/p/${session.id}`;
  const readOnly = useReadOnly();
  const openRecord = useOpenRecord();

  const labels = newRecordLabels(collections);
  const newItems: MenuItem[] = [
    ...collections.map<MenuItem>((c, i) => ({
      key: c.id,
      label: capitalize(labels[i]!.noun),
      hint: labels[i]!.hint,
      icon: <Icon token={c.icon} />,
      onSelect: () => openRecord(project.createRecord(c.id, { title: '' })),
    })),
    { type: 'separator' },
    ...(meta.sections.includes('votes') ? [{ label: 'Poll', icon: <Vote />, onSelect: () => navigate(`${base}/votes?new=1`) } as MenuItem] : []),
    ...(meta.sections.includes('boards')
      ? [
          {
            label: 'Board',
            icon: <Shapes />,
            onSelect: () => navigate(`${base}/boards/${project.createBoard({ name: 'Untitled board' })}`),
          } as MenuItem,
        ]
      : []),
    { label: 'Collection…', icon: <Plus />, onSelect: () => openNewCollection() },
  ];

  return (
    <div className="page overview">
      <header className="ov-hero">
        {/* The icon, name and pitch are edited in project settings only. */}
        <span className="ov-hero__tile">
          <ProjectTile icon={meta.icon} color={meta.color} size={60} />
        </span>
        <div className="ov-hero__text">
          <h1 className="ov-hero__name">{meta.name}</h1>
          {meta.description ? (
            <p className="ov-hero__desc">{meta.description}</p>
          ) : (
            !readOnly && (
              <p className="ov-hero__desc ov-hero__desc--empty">Add a one-line pitch in project settings</p>
            )
          )}
          <div className="ov-hero__meta">
            <Badge size="sm" tone={sync.mode === 'cloud' ? 'accent' : 'neutral'}>
              {sync.mode === 'cloud' ? 'Cloud project' : 'On this device'}
            </Badge>
            {readOnly && (
              <Badge size="sm" variant="outline" icon={<Eye />}>
                View only
              </Badge>
            )}
            {meta.createdAt > 0 && <span>Started {formatDate(meta.createdAt, { month: 'long', day: 'numeric' })}</span>}
          </div>
        </div>
        {!readOnly && (
          <Menu
            placement="bottom-end"
            minWidth={260}
            trigger={
              <Button variant="primary" icon={<Plus />}>
                New
              </Button>
            }
            items={newItems}
          />
        )}
      </header>

      <div className="ov-grid">
        <CollectionsCard collections={collections} />
        {meta.sections.includes('votes') && <VotesCard />}
        <RecentCard collections={collections} />
        {meta.sections.includes('boards') && <BoardsCard />}
      </div>
    </div>
  );
}

function CardHead({ icon, title, to, action }: { icon: ReactNode; title: string; to?: string; action?: string }) {
  return (
    <div className="ov-card__head">
      <span className="ov-card__icon">{icon}</span>
      <h2 className="ov-card__title">{title}</h2>
      {to && (
        <Link to={to} className="ov-card__link">
          {action ?? 'Open'} <ArrowRight size={13} />
        </Link>
      )}
    </div>
  );
}

/** Every collection, in the same order as the sidebar. */
function CollectionsCard({ collections }: { collections: CollectionData[] }) {
  const session = useSession();
  const project = useProject();
  const version = useProjectVersion();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const counts = useMemo(() => project.counts(), [project, version]);
  const readOnly = useReadOnly();
  return (
    <Card className="ov-card ov-card--codex" padding="lg">
      <CardHead icon={<LayoutGrid size={16} />} title="Collections" />
      <div className="codex-tiles">
        {collections.map((c) => (
          <Link key={c.id} to={`/p/${session.id}/c/${c.id}`} className="codex-tile" data-color={c.color}>
            <span className="codex-tile__icon">
              <Icon token={c.icon} size={18} />
            </span>
            <span className="codex-tile__name">{c.name}</span>
            <span className="codex-tile__count tabular">{counts[c.id] ?? 0}</span>
          </Link>
        ))}
        {!readOnly && (
          <button type="button" className="codex-tile codex-tile--add" onClick={() => openNewCollection()}>
            <span className="codex-tile__icon">
              <Plus size={18} />
            </span>
            <span className="codex-tile__name">New collection</span>
          </button>
        )}
      </div>
    </Card>
  );
}


function VotesCard() {
  const session = useSession();
  const polls = usePolls();
  const openPolls = polls.filter((p) => p.status === 'open').slice(0, 3);
  const decided = polls.filter((p) => p.status === 'closed').length;
  return (
    <Card className="ov-card ov-card--votes" padding="lg">
      <CardHead icon={<Vote size={16} />} title="Open votes" to={`/p/${session.id}/votes`} action="All votes" />
      {openPolls.length === 0 ? (
        <p className="ov-muted">No open votes. {decided ? `${decided} decided so far.` : 'Ask the team to decide something.'}</p>
      ) : (
        <div className="ov-polls">
          {openPolls.map((p) => {
            const r = pollResults(p);
            const lead = r.options[0];
            return (
              <Link key={p.id} to={`/p/${session.id}/votes#${p.id}`} className="ov-poll">
                <span className="ov-poll__q">{p.question}</span>
                <span className="ov-poll__lead">
                  {lead && r.voters > 0 ? (
                    <>
                      Leading: <b>{lead.label}</b>
                    </>
                  ) : (
                    'No votes yet'
                  )}
                  <span className="ov-poll__count tabular">
                    {r.voters} voter{r.voters === 1 ? '' : 's'}
                  </span>
                </span>
              </Link>
            );
          })}
        </div>
      )}
    </Card>
  );
}


function RecordRow({ record, collection, hint }: { record: RecordData; collection: CollectionData; hint?: string }) {
  const people = usePersonMap();
  const editor = record.updatedBy ? people.get(record.updatedBy) : undefined;
  return (
    <RecordLink id={record.id} className="ov-row">
      <span className="ov-row__icon" data-color={collection.color}>
        <Icon token={record.icon ?? collection.icon} size={15} />
      </span>
      <span className="ov-row__title g-truncate">{record.title || 'Untitled'}</span>
      <span className="ov-row__hint">{hint ?? collection.name}</span>
      {editor && !hint && <Avatar name={editor.name} color={editor.color} size={18} />}
    </RecordLink>
  );
}

function RecentCard({ collections }: { collections: CollectionData[] }) {
  const all = useAllRecords();
  const byId = useMemo(() => new Map(collections.map((c) => [c.id, c])), [collections]);
  const recent = useMemo(() => [...all].sort((a, b) => b.updatedAt - a.updatedAt).slice(0, 7), [all]);
  return (
    <Card className="ov-card ov-card--recent" padding="lg">
      <CardHead icon={<Clock size={16} />} title="Recently edited" />
      <div className="ov-list">
        {recent.map((r) => {
          const c = byId.get(r.collectionId);
          return c ? (
            <div key={r.id} className="ov-recent">
              <RecordRow record={r} collection={c} />
              <span className="ov-recent__time">{relativeTime(r.updatedAt)}</span>
            </div>
          ) : null;
        })}
        {!recent.length && <p className="ov-muted">Nothing yet.</p>}
      </div>
    </Card>
  );
}


function BoardsCard() {
  const session = useSession();
  const boards = useBoards();
  const project = useProject();
  return (
    <Card className="ov-card ov-card--boards" padding="lg">
      <CardHead icon={<Shapes size={16} />} title="Boards" to={`/p/${session.id}/boards`} action="All boards" />
      <div className="ov-list">
        {boards.slice(0, 4).map((b) => {
          const notes = project.listBoardItems(b.id).filter((i) => i.type === 'note').length;
          return (
            <Link key={b.id} to={`/p/${session.id}/boards/${b.id}`} className="ov-row">
              <span className="ov-row__icon" data-color="yellow">
                <Shapes size={15} />
              </span>
              <span className="ov-row__title g-truncate">{b.name}</span>
              <span className="ov-row__hint tabular">{notes} notes</span>
            </Link>
          );
        })}
        {!boards.length && <p className="ov-muted">No boards yet.</p>}
      </div>
    </Card>
  );
}
